import { TableInputContainer } from "../../../../../../components/tableInputContainer/TableInputContainer";
import { DataButton, InputCheckbox, InputNumber } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { useState } from "react";
import { EntityType } from "../../../../stores/SellarCreditOrderStore";
import { InputSelect, InputOption } from "@sellar-io/components";
import { useLegalEntity } from "../../../../../../hooks/api/v2/legalEntity/useLegalEntity";
import { useSellarCreditStatementStore } from "../../../../stores/SellarCreditStatementStore";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react";
import { capitalize, truncate } from "@sellar-io/common";
import type { InvoiceStatusEnumType } from "@sellar-io/api-v2/types";
import { Icon } from "../../../../../../components";
import { useLegalEntityId } from "../../../../../../hooks/api/v2/legalEntity/useLegalEntityId";

export const SellarCreditStatementFilter = ({
  onDismiss,
  isMobile,
  setClickOutside,
}: {
  onDismiss: () => void;
  isMobile?: boolean;
  setClickOutside?: (value?: boolean) => void;
}) => {
  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });

  const users = Array.from(
    new Map(
      legalEntity?.businessList
        .flatMap((business) =>
          business.businessUsersWithCreditAccess.map((user) => ({
            id: user.UserId,
            name: user.name,
          })),
        )
        .map((user) => [user.id, user]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const state = useSellarCreditStatementStore(useShallow((state) => state));

  const [business, setBusiness] = useState<EntityType | null>(state.business);
  const [documentTotalMin, setDocumentTotalMin] = useState(
    state.documentTotalMin,
  );
  const [documentTotalMax, setDocumentTotalMax] = useState(
    state.documentTotalMax,
  );
  const [excludeCreditNotes, setExcludeCreditNotes] = useState(
    state.excludeCreditNotes,
  );
  const [paymentStatuses, setPaymentStatuses] = useState(state.paymentStatuses);
  const [user, setUser] = useState<EntityType | null>(state.user);

  const filtersApplied =
    state.business !== null ||
    state.user !== null ||
    state.documentTotalMin !== "" ||
    state.documentTotalMax !== "" ||
    state.excludeCreditNotes ||
    state.paymentStatuses.length > 0;

  const disabled =
    business === state.business &&
    user === state.user &&
    documentTotalMin === state.documentTotalMin &&
    documentTotalMax === state.documentTotalMax &&
    excludeCreditNotes === state.excludeCreditNotes &&
    paymentStatuses === state.paymentStatuses;

  const onApply = (reset: boolean) => {
    if (reset) {
      useSellarCreditStatementStore.getState().reset();
    } else {
      useSellarCreditStatementStore.getState().setState({
        ...state,
        business,
        documentTotalMin,
        documentTotalMax,
        excludeCreditNotes,
        paymentStatuses,
        user,
      });
    }
    onDismiss();
  };

  const paymentStatusesValues: InvoiceStatusEnumType[] = [
    "issued",
    "payment_processing",
    "paid",
    "void",
  ];

  return (
    <div>
      <div
        className={twMerge(
          "w-full",
          isMobile ? "px-4 pt-2" : "border-b border-zinc-300 p-4",
        )}
      >
        <span className="font-semibold">Filter documents</span>
      </div>
      <div className="w-full p-4">
        <div className="flex flex-col gap-3">
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">Business</div>
            <div className="w-[55%]">
              <TableInputContainer>
                <InputSelect
                  defaultValue={state.business?.id ?? undefined}
                  onChange={(e: { target: { value?: string } }) => {
                    const value = e.target.value;
                    const id = value ? Number.parseInt(value) : null;
                    let business = null;
                    if (id) {
                      business = legalEntity?.businessList.find(
                        (business) => business.id === id,
                      );
                    }
                    setBusiness(business ?? null);
                  }}
                  className="text-sm font-medium"
                >
                  <InputOption>All businesses</InputOption>
                  {legalEntity?.businessList.map((business, idx) => (
                    <InputOption key={idx} value={business.id}>
                      {business.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </TableInputContainer>
            </div>
          </div>

          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">User</div>
            <div className="w-[55%]">
              <TableInputContainer>
                <InputSelect
                  defaultValue={state.user?.id ?? undefined}
                  onChange={(e: { target: { value?: string } }) => {
                    const value = e.target.value;
                    const id = value ? Number.parseInt(value) : null;
                    let user = null;
                    if (id) {
                      user = users.find((user) => user.id === id);
                    }
                    setUser(user ?? null);
                  }}
                  className="text-sm font-medium"
                >
                  <InputOption>All users</InputOption>
                  {users.map((user, idx) => (
                    <InputOption key={idx} value={user.id}>
                      {user.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </TableInputContainer>
            </div>
          </div>

          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">Payment status</div>
            <div className="w-[55%]">
              <Listbox
                value={paymentStatuses}
                onChange={setPaymentStatuses}
                multiple
              >
                <ListboxButton
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                >
                  {paymentStatuses.length > 0
                    ? truncate(
                        paymentStatuses
                          .map((status) => capitalize(status))
                          .join(", "),
                      )
                    : "All statuses"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {paymentStatusesValues.map((paymentStatus, i) => (
                    <ListboxOption
                      key={i}
                      value={paymentStatus}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      {paymentStatuses.includes(paymentStatus) ? (
                        <Icon icon="CHECK" size={0.7} />
                      ) : (
                        <span className="w-[16.8px]" />
                      )}
                      {capitalize(paymentStatus)}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>

          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Order total</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <div className="flex items-center">
                <span className="mr-2 inline-block w-[60px] text-sm font-medium">
                  Min.
                </span>
                <TableInputContainer className="w-full">
                  <span className="pl-[2px] leading-none font-medium text-zinc-400">
                    £
                  </span>
                  <InputNumber
                    defaultValue={documentTotalMin}
                    min="0"
                    onChange={(e) => setDocumentTotalMin(e.target.value)}
                    className="h-[19px] text-sm font-medium"
                  />
                </TableInputContainer>
              </div>
              <div className="flex items-center">
                <span className="mr-2 inline-block w-[60px] text-sm font-medium">
                  Max.
                </span>
                <TableInputContainer className="w-full">
                  <span className="pl-[2px] leading-none font-medium text-zinc-400">
                    £
                  </span>
                  <InputNumber
                    defaultValue={documentTotalMax}
                    min={documentTotalMin}
                    onChange={(e) => {
                      const maxVal = parseInt(e.target.value);
                      if (maxVal <= parseInt(documentTotalMin)) return;
                      setDocumentTotalMax(e.target.value);
                    }}
                    className="h-[19px] text-sm font-medium"
                  />
                </TableInputContainer>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Exclude credit notes</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <label className="flex cursor-pointer items-start text-sm font-medium">
                <InputCheckbox
                  checked={excludeCreditNotes}
                  onChange={(e) => setExcludeCreditNotes(e.target.checked)}
                />
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center gap-2 p-4">
        <DataButton
          onClick={() => onApply(false)}
          border={true}
          disabled={disabled}
        >
          {"Apply"}
        </DataButton>
        {!!filtersApplied && (
          <DataButton color="transparent" onClick={() => onApply(true)}>
            Clear all
          </DataButton>
        )}
      </div>
    </div>
  );
};
